import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import userService, { PanelAccess, RoleDefaultAccess } from '../../../shared/services/userService';
import { computeEffectivePanelAccess } from '../panelAccess/effectivePanelAccess';

function boolFromApi(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

function normalizePanelRows(rows: PanelAccess[]): PanelAccess[] {
  return (rows || []).map((row) => ({
    ...row,
    has_access: boolFromApi((row as { has_access?: unknown }).has_access),
  }));
}

function normalizeRoleRows(rows: RoleDefaultAccess[]): RoleDefaultAccess[] {
  return (rows || []).map((row) => ({
    ...row,
    has_access: boolFromApi((row as { has_access?: unknown }).has_access),
  }));
}

function extractActiveReferentFunctions(
  data: Array<{ function?: string; is_active?: boolean | number }> | undefined,
): string[] {
  if (!data?.length) return [];
  return data
    .filter((r) => r.is_active === true || r.is_active === 1)
    .map((r) => String(r.function || '').trim())
    .filter(Boolean);
}

/**
 * Charge overrides utilisateur, défauts de rôle et fonctions référent actives.
 */
export function usePanelSectionAccess(enabled: boolean) {
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const [panelAccess, setPanelAccess] = useState<PanelAccess[]>([]);
  const [roleDefaultAccess, setRoleDefaultAccess] = useState<RoleDefaultAccess[]>([]);
  const [referentFunctions, setReferentFunctions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !currentUser?.id || !currentUser?.roles_id) {
      setPanelAccess([]);
      setRoleDefaultAccess([]);
      setReferentFunctions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      userService.getUserPanelAccess(currentUser.id).catch(() => ({ success: false as const, data: [] as PanelAccess[] })),
      userService.getRoleDefaultAccess(currentUser.roles_id).catch(() => ({ success: false as const, data: [] as RoleDefaultAccess[] })),
      userService.getMyReferents().catch(() => ({ success: false as const, data: [] })),
    ])
      .then(([userRes, roleRes, referentRes]) => {
        if (cancelled) return;
        if (userRes.success && userRes.data) {
          setPanelAccess(normalizePanelRows(userRes.data));
        } else {
          setPanelAccess([]);
        }
        if (roleRes.success && roleRes.data) {
          setRoleDefaultAccess(normalizeRoleRows(roleRes.data));
        } else {
          setRoleDefaultAccess([]);
        }
        if (referentRes.success && referentRes.data) {
          setReferentFunctions(extractActiveReferentFunctions(referentRes.data));
        } else {
          setReferentFunctions([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPanelAccess([]);
          setRoleDefaultAccess([]);
          setReferentFunctions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, currentUser?.id, currentUser?.roles_id]);

  const hasAccessToSection = useCallback(
    (sectionId: string): boolean => {
      if (!currentUser?.roles_id) return false;
      return computeEffectivePanelAccess(
        sectionId,
        currentUser.roles_id,
        panelAccess,
        roleDefaultAccess,
        referentFunctions,
      );
    },
    [currentUser, panelAccess, roleDefaultAccess, referentFunctions],
  );

  const hasReferentFunction = useCallback(
    (referentFunction: string): boolean => {
      const key = referentFunction.trim();
      if (!key) return false;
      return referentFunctions.includes(key);
    },
    [referentFunctions],
  );

  return {
    loading,
    hasAccessToSection,
    hasReferentFunction,
    referentFunctions,
    panelAccess,
    roleDefaultAccess,
  };
}
